import React, { useMemo, useRef } from 'react';
import ExpListingCard from 'components/exp-listing-card';
import Link from 'components/link';
import Listing, { ListingSerializableProperties } from 'data/listing/client-app/types/listing';
import { useOnListingCardClick } from 'utils/listing-card';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import styles from './style.module.scss';

export interface CityListingsCarouselProps {
  listings?: ListingSerializableProperties[] | null;
  areaName?: string;
  /** Href for both the CTA card's button and the fallback "View Listings" link. */
  viewListingsUrl: string;
  isCrawler: boolean;
}

/**
 * Homepage-style listings carousel (scroll-snap + arrow buttons, same
 * mechanics as `home-page/local-focus/listings-section`) for a city-info
 * page, with a trailing CTA card ("Ready to explore {areaName}?") as the
 * final slide instead of a separate static CTA section.
 */
const CityListingsCarousel = ({ listings, areaName, viewListingsUrl, isCrawler }: CityListingsCarouselProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const onListingCardClick = useOnListingCardClick();
  const { t } = useTranslation(Namespaces.COMMON);

  const listingCardData = useMemo(() => {
    return (listings ?? [])
      .map(data => Listing.fromJSON(data))
      .filter(listing => listing?.id && listing.isImageReady);
  }, [listings]);

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: direction === 'right' ? 340 : -340, behavior: 'smooth' });
  };

  const area = areaName || t(LOCALE_COMMON_PATHS.CITYINFO_CITYLISTINGSCAROUSEL_DEFAULTAREA, { defaultValue: 'this area' });

  return (
    <section className={styles.component}>
      <h2 className={styles.heading}>
        {listingCardData.length > 0
          ? t(LOCALE_COMMON_PATHS.CITYINFO_CITYLISTINGSCAROUSEL_HEADINGWITHLISTINGS, { area, defaultValue: 'Homes for sale in {{area}}' })
          : t(LOCALE_COMMON_PATHS.CITYINFO_CITYLISTINGSCAROUSEL_HEADINGNOLISTINGS, { area, defaultValue: 'Ready to explore {{area}}?' })}
      </h2>

      <div className={styles.carouselWrapper}>
        {listingCardData.length > 0 && (
          <button
            type="button"
            className={buildClassName(styles.carouselArrow, styles.arrowLeft)}
            onClick={() => scrollCarousel('left')}
            aria-label={t(LOCALE_COMMON_PATHS.CITYINFO_CITYLISTINGSCAROUSEL_SCROLLLEFTARIALABEL, { defaultValue: 'Scroll left' })}
          >
            <svg viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        )}

        <div className={styles.carouselScroller} ref={scrollerRef}>
          {listingCardData.map((listing, index) => (
            <div key={listing.id || index} className={styles.cardWrapper}>
              <ExpListingCard
                listing={listing}
                isCrawler={isCrawler}
                onListingCardClick={onListingCardClick}
                compactOverlay
              />
            </div>
          ))}

          <div className={styles.cardWrapper}>
            <div className={styles.ctaCard}>
              <h3 className={styles.ctaHeading}>{t(LOCALE_COMMON_PATHS.CITYINFO_CITYLISTINGSCAROUSEL_CTAHEADING, { area, defaultValue: 'Ready to explore {{area}}?' })}</h3>
              <p className={styles.ctaText}>{t(LOCALE_COMMON_PATHS.CITYINFO_CITYLISTINGSCAROUSEL_CTATEXT, { defaultValue: 'Browse available listings and find your perfect home.' })}</p>
              <Link href={viewListingsUrl} className={styles.ctaButton}>{t(LOCALE_COMMON_PATHS.CITYINFO_CITYLISTINGSCAROUSEL_CTABUTTON, { defaultValue: 'View Listings' })}</Link>
            </div>
          </div>
        </div>

        {listingCardData.length > 0 && (
          <button
            type="button"
            className={buildClassName(styles.carouselArrow, styles.arrowRight)}
            onClick={() => scrollCarousel('right')}
            aria-label={t(LOCALE_COMMON_PATHS.CITYINFO_CITYLISTINGSCAROUSEL_SCROLLRIGHTARIALABEL, { defaultValue: 'Scroll right' })}
          >
            <svg viewBox="0 0 24 24" fill="none"><path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        )}
      </div>
    </section>
  );
};

export default CityListingsCarousel;
